import { observable, action } from 'mobx';
import agent from '../services/web/agent';
import { SmtpSettingsEditMode } from '../services/enums/smtpSettings';
import { EncryptionTypesEnum } from '../services/enums/encryptionTypesEnum';
import commonValidators from '../services/validations/commonValidators';
import userSharedFormsValidator from '../services/validations/userSharedFormsValidator';

class SmtpSettingsStore {

    formErrors = [];

    @observable hostValidationMessage;
    @observable portValidationMessage;
    @observable fromAddressValidationMessage;
    @observable fromDisplayNameValidationMessage;
    @observable usernameValidationMessage;
    @observable passwordValidationMessage;
    @observable saveSmtpSettingsErrorMessage;
    @observable savingSmtpSettings;
    @observable sendingTestEmail;

    @observable testSmtpEmailValidationMessage;
    @observable sendTestEmailErrorMessage;
    @observable lastUsedTestEmailAddress;

    @observable values = {
        id: null,
        host: '',
        port: 25,
        encryptionType: null,
        fromAddress: '',
        displayName: '',
        useDefaultCredentials: false,
        username: '',
        password: '',
        smtpSettingsEditMode: SmtpSettingsEditMode.none,
        testSmtpEmail: ''
    };

    buildSmtpSettingsModel() {

        let smtpSettings = {
            host: this.values.host,
            port: this.values.port,
            encryptionType: this.values.encryptionType,
            fromAddress: this.values.fromAddress,
            displayName: this.values.displayName?.trim(),
            useDefaultCredentials: this.values.useDefaultCredentials,
            host: this.values.host
        };

        if (!this.values.useDefaultCredentials) {
            smtpSettings.username = this.values.username;
            smtpSettings.password = this.values.password;
        }

        return smtpSettings;
    }

    validateHost() {

        const validation = commonValidators.validateGenericRequiredField(this.values.host);
        
        this.hostValidationMessage = validation.errors.first('field');

        this.formErrors.push(validation.errorCount);
    }

    validatePort() {

        const validation = commonValidators.validatePort(this.values.port);

        this.portValidationMessage = validation.errors.first('port');

        this.formErrors.push(validation.errorCount);
    }

    validateFromAddress() {

        const validation = commonValidators.validateEmail(this.values.fromAddress);

        this.fromAddressValidationMessage = validation.errors.first('email');

        this.formErrors.push(validation.errorCount);
    }

    validateUsername() {

        if (!this.values.useDefaultCredentials) {

            const validation = userSharedFormsValidator.validateUserName(this.values.username);

            this.usernameValidationMessage = validation.errors.first('userName');

            this.formErrors.push(validation.errorCount);
        }
    }

    validatePassword() {

        if (!this.values.useDefaultCredentials) {

            const validation = userSharedFormsValidator.validateGenericRequiredField(this.values.password);

            this.passwordValidationMessage = validation.errors.first('field');

            this.formErrors.push(validation.errorCount);
        }
    }

    validateTestSmtpEmail() {

        const validation = commonValidators.validateEmail(this.values.testSmtpEmail);

        this.testSmtpEmailValidationMessage = validation.errors.first('email');

        return validation.errorCount < 1;
    }

    @action emptyAllFieldValidationMessages() {

        this.hostValidationMessage = '';
        this.portValidationMessage = '';
        this.fromAddressValidationMessage = '';
        this.fromDisplayNameValidationMessage = '';
        this.usernameValidationMessage = '';
        this.passwordValidationMessage = '';
    }

    @action validateSmtpSettingsForm() {

        this.formErrors = [];
        this.validateHost();
        this.validatePort();
        this.validateFromAddress();
        this.validateUsername();
        this.validatePassword();

        if (this.formErrors.filter((valid) => valid > 0).length > 0) {
            return false;
        }

        return true;
    }

    @action getSmtpSettings() {

        this.loadingSmtpSettings = true;

        return agent.SmtpSettings.getSmtpSettings()

            .then(action((smtpSettingsResponse) => {

                if (smtpSettingsResponse.length > 0) {

                    let smtpSettings = smtpSettingsResponse[0];

                    this.values.id = smtpSettings.id;
                    this.values.host = smtpSettings.host;
                    this.values.port = smtpSettings.port;
                    this.values.encryptionType = smtpSettings.encryptionType;
                    this.values.fromAddress = smtpSettings.fromAddress;
                    this.values.displayName = smtpSettings.displayName;
                    this.values.useDefaultCredentials = smtpSettings.useDefaultCredentials;
                    this.values.username = smtpSettings.username ? smtpSettings.username : '';

                    this.values.smtpSettingsEditMode = SmtpSettingsEditMode.update;
                }
                else {

                    this.values.smtpSettingsEditMode = SmtpSettingsEditMode.add;
                }

                this.loadingSmtpSettings = false;

            }));
    }

    @action SaveSmtpSettings() {

        if (this.values.id > 0) {
            return this.updateSmtpSettings();
        }
        else {
            return this.addSmtpSettings();
        }
    }

    @action SendTestEmail() {

        this.sendingTestEmail = true;

        var testConnectionModel = this.buildSmtpSettingsModel();
        testConnectionModel.recieverEmail = this.values.testSmtpEmail;
        this.lastUsedTestEmailAddress = this.values.testSmtpEmail;
        return agent.SmtpSettings.testConnection(testConnectionModel)
            .catch(action((err) => {
                if (err && err.response && err.response.text) {

                    this.sendTestEmailErrorMessage = JSON.parse(err.response.text).Message;
                }

                throw err;
            }))
            .finally(() => {
                this.sendingTestEmail = false;
            });
    }

    @action addSmtpSettings() {

        this.savingSmtpSettings = true;
        this.saveSmtpSettingsErrorMessage = '';

        return agent.SmtpSettings.createSmtpSettings(this.buildSmtpSettingsModel())
            .then(() => {
                this.getSmtpSettings();
            })
            .catch(action((err) => {

                if (err && err.response && err.response.text) {

                    this.saveSmtpSettingsErrorMessage = JSON.parse(err.response.text).Message;
                }

                throw err;
            }))
            .finally(() => {
                this.savingSmtpSettings = false;
            });
    }

    @action updateSmtpSettings() {

        this.savingSmtpSettings = true;
        this.saveSmtpSettingsErrorMessage = '';

        return agent.SmtpSettings.updateSmtpSettings(this.values.id, this.buildSmtpSettingsModel())
            .then(() => {
                this.getSmtpSettings();
            })
            .catch(action((err) => {

                if (err && err.response && err.response.text) {

                    this.saveSmtpSettingsErrorMessage = JSON.parse(err.response.text).Message;
                }

                throw err;
            }))
            .finally(() => {
                this.savingSmtpSettings = false;
            });
    }

    @action setPort(port) {
        this.values.port = port.trim();
    }

    @action setPassword(password) {
        this.values.password = password;
    }

    @action setHost(host) {
        this.values.host = host.trim();
    }

    @action setEncryptionType(encryptionType) {

        this.values.encryptionType = encryptionType;
        switch (encryptionType) {
            case EncryptionTypesEnum.Tls:
                this.values.port = 587;
                break;
            case EncryptionTypesEnum.Ssl:
                this.values.port = 465;
                break;
            default:
                this.values.port = 25;
        }

        this.portValidationMessage = '';
    }

    @action setFromAddress(fromAddress) {
        this.values.fromAddress = fromAddress.trim();
    }

    @action setFromDisplayName(displayName) {
        this.values.displayName = displayName;
    }

    @action setUseDefaultCredentials(useDefaultCredentials) {
        this.values.useDefaultCredentials = useDefaultCredentials;

        if (useDefaultCredentials) {
            this.usernameValidationMessage = '';
            this.passwordValidationMessage = '';
        }
    }

    @action setUsername(username) {
        this.values.username = username.trim();
    }

    @action setSaveSmtpSettingsErrorMessage(errorMessage) {

        this.saveSmtpSettingsErrorMessage = errorMessage;
    }

    @action setTestSmtpEmailValidationMessage(errorMessage) {

        this.testSmtpEmailValidationMessage = errorMessage;
    }

    @action setSendTestEmailErrorMessage(errorMessage) {

        this.sendTestEmailErrorMessage = errorMessage;
    }

    @action setTestSmtpEmail(testSmtpEmail) {

        this.values.testSmtpEmail = testSmtpEmail.trim();
    }
}

export default new SmtpSettingsStore();


// WEBPACK FOOTER //
// ./src/stores/smtpSettingsStore.js